/**
 * @file
 * visitor-tracking-client for tracking and recording
 * visitor interactions with a website.<br><br>
 *
 * HELLO USER READ THIS PLEASE. Welcome.
 * This header part of the file is the README. Please
 * read it before reading other things. The comments for each function are
 * very verbose, so please read them too, after this. Right now, this is
 * version 1.0 of VisitorTracker. Its ONLY use is as an API. It only talks to
 * the backend and makes ajax calls. It allows for session/visitor creation
 * and for event tracking.<br><br>
 *
 * documentation:<br>
 * https://visitortracking.staging.billsdev.com/
 * visitortracking/jsondoc-ui.html#<br>
 * This documentation is not that descriptive, but it does show you
 * the types of ajax calls that this API makes.<br><br>
 *
 * If this url changes, this frontend will not work.
 * The only two functions you should be using in this
 * API are init and trackEvent. init must be run right after you create the
 * VisitorTracker object. init sets up the session and visitor creation and
 * the cookies. It reads data from the url and from the cookies, and creates
 * cookies as well called visitor-id and session-id. trackevent sends
 * data to the backend, and you pass in parameters for it describing the type
 * of event being sent and any details about the event that you want to
 * describe.<br><br>
 *
 * This API is basically an easy way to track visitor and session
 * data, but also provides a modular way to send data (and it also
 * reads some data by itself) about events<br><br>
 *
 * All the functions are labeled with public in their comments. However,
 * the only three you should be using are init, defineEvent and trackEvent.
 * Most of the other functions are ajax calls,
 * the rest are just helper methods.
 * They are also verbosely documented, and are pretty easy to follow.<br><br>
 *
 * All functions and objects listed here are all members of VisitorTracker,
 * whether they are Global or not. Putting them as Global makes it easier to
 * look through them all in the jsdoc. However, the methods inside the Event class
 * are Event class methods and can only be called by Event objects<br><br>
 *
 * Other important links:<br><br>
 *
 * google doc for changes to visitor tracking apis<br>
 * https://docs.google.com/document/d/
 * 1JlmettRSpz19GSLDkhxJy6lSSfTkABy755yZlCVfuIU/edit<br><br>
 *
 * reference for object data being stored on server<br>
 * https://billsdev.atlassian.net/wiki/display/DW/Visitor+Tracking+Data<br><br>
 *
 * bitbucket link to frontend repo<br>
 * https://bitbucket.org/billsdotcom_bills/visitor-tracking-client<br><br>
 *
 * That's it. Happy tracking.

 * @example <caption> How to initialize and use VisitorTracker
 * to track Events, parenting </caption>
 * // create a VisitorTracker object
 * var vt = new VisitorTracker();
 * // init vt
 * vt.init({},'');
 *
 * // define a click event with no parent with a lead_id specified as leadIdValue
 * var parent1 = vt.defineEvent('click', {lead_id: 'leadIdValue'}, null);
 * // define a click event with parent of parent1
 * var child1 = vt.defineEvent('click', {lead_id: 'leadIdValuechild'}, parent1);
 *
 * // track the event
 * parent1 = vt.trackEvent(parent1, {event_value: 'page'});
 * $('#buttonPressID').click(function() {
 *    child1 = vt.trackEvent(child1, {event_value: 'clicked'});
 *  });
 * @author Joshua Bollar
 * @version July 06, 2015
 */

(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD. Register as an anonymous module.
    define(['jquery', 'jQuery-ajaxTransport-XDomainRequest'], factory);
  } else if (typeof exports === 'object') {
    // Node. Does not work with strict CommonJS, but
    // only CommonJS-like environments that support module.exports,
    // like Node.
    module.exports = factory(require('jquery'), require('jQuery-ajaxTransport-XDomainRequest'));
  } else {
    // Browser globals (root is window)
    root.VisitorTracker = factory(root.$);
  }
}(this, function ($) {
  'use strict';
  /**
   *
   * The Visitor Tracker object<br>
   * Description: This is the object that all the functions are defined
   * for and contains the visitorObject and sessionObject that we read in
   * from the server<br><br>
   *
   * NOTE: the ajax calls all use a callback. Example:<br>
   *       postSession({}, function (data) {<br>
   *        // access and use the data returned from the postSession here<br>
   *       });<br><br>
   *
   *       In order to properly use ajax callbacks, all variables before
   *       the callback must be truthy in order for the function to execute
   *       properly.
   *
   * @example <caption> How to initialize and use VisitorTracker </caption>
   * // create a VisitorTracker object
   * var vt = new VisitorTracker();
   * // init vt
   * vt.init({},'');
   * // track an event with a lead_id specified as leadIdValue with no parent
   * var event1 = vt.defineEvent('eventType', {lead_id: 'leadId'}, null)
   * event1 = vt.trackEvent(event1, {event_value = 'success'});
   * @property {object} visitorObject the object for storing the Visitor data
   * @property {object} sessionObject the object for storing the Session data
   * @propery {string} environment the string for the work environment
   * @property {boolean} ie are we running in ie?
   * @property {boolean} sessionCreated has session been created
   * @class VisitorTracker
   */
  function VisitorTracker() {
    this.intervalId = 0;
    this.visitorObject = {};
    this.sessionObject = {};
    this.environment = '';
    this.ie = !($.support.cors || !$.ajaxTransport || !window.XDomainRequest);
    this.sessionCreated = false;
    this.sessionData = null;
    this.version = 'v1.6.0';

    /**
     * A Visitor object<br><br>
     *
     * Description: This is the object payload for what can be sent to the
     * server in an ajax call for a Visitor object<br><br>
     *
     * the available ajax functions for a Visitor object are<br>
     * GET /visitors/{visitorId}<br>
     * PUT /visitors/{visitorId}<br>
     * DELETE /visitors/{visitorId}<br><br>
     *
     * link to documentation for backend:<br>
     * https://visitortracking.staging.billsdev.com/
     * visitortracking/jsondoc-ui.html#
     *
     * <br><br>NOTE: you cannot POST a visitor object.
     * When you POST a session, a Visitor
     * object automatically gets created and can then be accessed through
     * the visitor_id parameter in the Session object.<br><br>
     *
     * descriptions for what each parameter in the Visitor object payload can be
     * found here:<br>
     * https://billsdev.atlassian.net/wiki/display/
     * DW/Visitor+Tracking+Data<br><br>
     *
     * This is the object payload of a visitor_id,
     * and contains the variables that
     * you are allowed to modify for a Visitor object
     * @global
     * @instance
     * @public
     */
    function Visitor() {
      this.browser = null;
      this.device = null;
      this.ip_address = null;
      this.os = null;
    }

    /**
     * A Session object<br><br>
     *
     * Description This is the object payload for what can be sent or received
     * from the server in an ajax call for a Session object to the server<br><br>
     *
     * the available ajax functions for a Session object are<br>
     * POST /session<br>
     * GET /session/{sessionId}<br>
     * PATCH /session/{sessionId}<br>
     * PUT /session/{sessionId}<br>
     * DELETE /session/{sessionId}<br><br>
     *
     * link to documentation for backend:<br>
     * https://visitortracking.staging.billsdev.com/
     * visitortracking/jsondoc-ui.html#<br><br>
     *
     * NOTE: when you POST a Session object, it automatically creates a Visitor
     * object that you can then get through the visitor-id stored in the Session
     * object<br><br>
     *
     * descriptions for what each parameter in the Session object that is stored
     * on the server can be found here:<br>
     * https://billsdev.atlassian.net/wiki/display/
     * DW/Visitor+Tracking+Data<br><br>
     *
     * This is the object payload of a session_id and contains the variables that
     * you are allowed to modify for a Session object.
     * @global
     * @instance
     * @public
     */
    function Session() {
      this.utm_http_referer = null;
      this.click_Id2 = null;
      this.utm_medium = null;
      this.utm_search_term = null;
      this.utm_sub5 = null;
      this.utm_account = null;
      this.utm_trans = null;
      this.utm_content = null;
      this.utm_campaign = null;
      this.experiment_id = null;
      this.utm_adgroup = null;
      this.utm_creative_type = null;
      this.utm_publisher = null;
      this.utm_term = null;
      this.utm_lander = null;
      this.utm_placement = null;
      this.utm_imagename = null;
      this.click_id = null;
      this.variation_id = null;
      this.host_name = null;
      this.utm_source = null;
      this.screen_size = null;
    }

    /**
     * An Event object<br><br>
     *
     * Description: This is the Event object.
     * It contains the eventPayload for what can be sent or received
     * from the server in an ajax call for an Event object to the server<br><br>
     *
     * the available ajax functions for an Event object are<br>
     * POST /events<br>
     * GET /events/parent/{parentEventId}<br>
     * GET /events/session/{sessionId}<br>
     * GET /events/{eventId}<br>
     * PATCH /events/{eventId}<br>
     * PUT /events/{eventId}<br>
     * DELETE /events/{eventId}<br><br>
     *
     * link to documentation for backend:<br>
     * https://visitortracking.staging.billsdev.com/
     * visitortracking/jsondoc-ui.html#<br><br>
     *
     * descriptions for what each parameter in the Event object that is stored
     * on the server can be found here:<br>
     * https://billsdev.atlassian.net/wiki/display/
     * DW/Visitor+Tracking+Data<br><br>
     *
     * This contains trigger functionality, on event_idDefined. This fills in
     * the event_id of the eventPayload on ajax call successful return. Then calls
     * addParentsToChildren.
     *
     * addParentsToChildren adds eventPayload.event_id as the parent_event_id of
     * every child Event stored in childrenList.
     * @global
     * @instance
     * @public
     * @class Event
     */
    function Event() {
      this.eventPayload = {
        parent_event_id : null,
        lead_id : null,
        page_url : null,
        page_name : null,
        event_page : null,
        event_action : null,
        event_element : null,
        event_element_name : null,
        event_value : null,
        event_id : null,
        additional_properties : {},
        time_difference: null,
        createdAt: new Date()
      };
      var discoverCount = 0;

      this.parentObject = false;
      this.childrenList = [];

      /**
       * Description: sets event_id and calls addParentsToChildren<br><br>
       *
       * Usage: to call addParentsToChildren after event_id is defined<br><br>
       *
       * Precondition: data and data.event_id must both be defined properly<br><br>
       *
       * Postcondition: addParentsToChildren is called. The trigger is turned off,
       *                so that event_id should not be changed again
       *
       * @public
       * @function on
       * @memberOf Event
       * @instance
       *
       * @param {object} eventObject the ev automatically sent in trigger event
       * @param {object} data the eventPayload object that contains the event_id
       */
      $(this).on('event_idDefined', function (eventObject, data) {

        this.set('event_id', data.event_id);
        this.addParentsToChildren();

        // turn off the trigger now!
        $(this).off('event_idDefined');
      });

      /**
       * Description: adds event_id as parent_event_id to all events in childrenList<br><br>
       *
       * Usage: so that children have parent_event_id properly defined<br><br>
       *
       * Precondition: event_id is defined<br><br>
       *
       * Postcondition: all events in childrenList have parent_event_id correctly defined
       *
       * @public
       * @function addParentsToChildren
       * @memberOf Event
       * @instance
       */
      this.addParentsToChildren = function() {

        for(var i = 0; i < this.childrenList.length; i++) {

          var currentChildData = this.childrenList[i];

          currentChildData.set('parent_event_id', this.get('event_id'));
        }
      };

      /**
       * Description: gets a value from eventPayload or additional_properties
       * based on the param given. if !eventPayload.dataToGet &&
       * !eventPayload.additional_properties.dataToGet, then undefined is returned <br><br>
       *
       * Usage: gets dataToGet from the eventPayload object<br><br>
       *
       * Precondition: eventPayload exists<br><br>
       *
       * Postcondition: returns value for key dataToGet or returns undefined
       *
       * @public
       * @function get
       * @memberOf Event
       * @instance
       *
       * @param {string} dataToGet the key for the value we want from eventPayload
       */
      this.get = function(dataToGet) {
        if(!dataToGet || typeof dataToGet !== 'string') {
          throw 'dataToGet is not defined properly in get in Event ' +
          'in VisitorTracker';
        }


        // we don't check for falsy, because eventPayload data is
        // default set to null. only check for undefined, does it
        // exist as a parameter of eventPayload
        if(this.eventPayload[dataToGet] !== undefined) {
          return this.eventPayload[dataToGet];
        } else {

          // check to see if it exists in additional_properties
          if(this.eventPayload.additional_properties[dataToGet]) {
            return this.eventPayload[dataToGet].additional_properties;
          } else {
            return undefined;
          }
        }
      };

      /**
       * Description: sets a value in the event payload based on the key
       * dataToSet and the value valueToSet. If a value is trying to be
       * set that does not exist in the eventPayload, it tries to see if
       * it exists in additional_properties. If it does not exist in
       * additional_properties, then it gets added to additional_properties
       * with dataToSet as key and valueToSet as value<br><br>
       *
       * Usage: to set variables in eventPayload<br><br>
       *
       * Precondition: dataToSet is a string, valueToSet is a string<br><br>
       *
       * Postcondition: eventPayload.dataToSet === valueToSet ||
       *                eventPayload.additional_properties.dataToSet === valueToSet
       *
       * @public
       * @function set
       * @memberOf Event
       * @instance
       *
       * @param {string} dataToSet the key to be accessed in eventPayload
       * @param {string} valueToSet the value of the key to be changed to in eventPayload
       */
      this.set = function(dataToSet, valueToSet) {
        if(!dataToSet || typeof dataToSet !== 'string') {
          throw 'dataToSet is not defined properly in get in Event ' +
          'in VisitorTracker';
        }

        if((!valueToSet || typeof valueToSet !== 'string') && dataToSet !== 'additional_properties') {
          throw 'valueToSet is not defined properly in get in Event ' +
          'in VisitorTracker';
        }

        // can't just say falsy, because eventPayload data is default
        // set to null
        if(this.eventPayload[dataToSet] !== undefined) {
          this.eventPayload[dataToSet] = valueToSet;
        } else {

          // just add it to additional properties
          this.eventPayload.additional_properties[dataToSet] = valueToSet;
          // setting just adds it to object, even if it doesn't exist
          // throw 'dataToSet does not exist in the event object you are looking at (cant get it)';
        }
      };
    }

    /**
     * Description: calls _postSession which sets the sessionObject and
     * visitorObject variables.<br><br>
     *
     * Usage: Initializes all of the variables for a current Visitor
     * and Session. Makes the VisitorTracker object fully, getting the data
     * for the Session and Visitor by creating a new one.
     * Used to create and initialize a VisitorTracker
     * object.<br><br>
     *
     * Precondition: no VisitorTracker object exists yet on the webpage<br><br>
     *
     * Postcondition: a VisitorTracker object is now initialized properly and
     *                exists on the webpage<br><br>
     * @public
     * @function init
     * @memberOf VisitorTracker
     * @instance
     * @global
     *
     * @example <caption> how variation_ids and
     * experiment_ids will be read </caption>
     * vt.init({
     *  variation_id: {
     *    varid1: 'varid1val',
     *    varid2: 'varid2val'
     *  },
     *  experiment_id: {
     *    experid1: 'experid1val'
     *  }
     * }, environValue, callbackValue);
     *
     * @param {object} addProps an object that contains variation_ids
     *                 and experiment_ids
     *                 from optimizely. Might contain more properties in the
     *                 future. No addProps are added if the session or visitor
     *                 already exists (the addProps param is basically ignored
     *                 if the session-id and visitor-id cookies already exist).
     *
     * @param {string} environ the environment that the api is using
     *
     * @param {function} callback the function that wants to use the data, uses
     *                   callback(data) to access the object data
     */
    this.init = function(addProps, environ, callback) {

      this._setEnvironment(environ);
      this.sessionData = this._getSessionData();

      // If URL contains vt_session_id, set to localStorage
      if (this.sessionData.vt_session_id) {
        this.set('vt_session_id', this.sessionData.vt_session_id);
      }

      // If we have vt_session_id saved in localStorage, make PUT request
      // otherwise, make POST request
      if (this.get('vt_session_id')) {
        var visitorId = this.get('vt_visitor_id');
        if (visitorId) {
          addProps.visitor_id = visitorId;
        }

        this._putSession(this.get('vt_session_id'), addProps, callback);
      } else {
        this._postSession(addProps, callback);
      }
    };

    /**
     * Description: Sets item to localStorage if defined<br><br>
     *
     * @public
     * @memberOf VisitorTracker
     *
     * @param {string} sessionId the sessionId to store
     */
    this.set = function (key, val) {
      try {
        if (window.localStorage) {
          window.localStorage.setItem(key, val);
        }
      } catch (err) {}
    };

    /**
     * Description: Get item from localStorage if defined<br><br>
     *
     * @public
     * @memberOf VisitorTracker
     */
    this.get = function (key) {
      try {
        if (window.localStorage) {
          return window.localStorage.getItem(key);
        }
      } catch (err) {}

      return undefined;
    };

    /**
     * Description: Sets the environment to 1 or 2 based on the environ
     * param given<br><br>
     *
     * Usage: Sets the environment<br><br>
     *
     * Precondition: environ is a valid environment type<br><br>
     *
     * Postcondition: environment is properly set in VisitorTracker object
     *
     * @public
     * @function _setEnvironment
     * @memberOf VisitorTracker
     * @instance
     * @global
     *
     * @param {string} environ the environment to be set to.
     */
    this._setEnvironment = function(environ) {
      // the two environments we are looking at
      var staging = 'https://visitortracking.staging.billsdev.com/' +
        'visitortracking/';
      var production = 'https://t.freedomfinancialnetwork.com/' +
        'visitortracking/';

      // the list of possible values for environment1
      var stagingList = ['local', 'development', 'test', 'staging'];

      // environ is falsy, we don't care about sandbox
      if(!environ || environ === 'sandbox') {
        this.environment = staging;

      } else { // environ is truthy

        // is it in the first environment list?
        if(stagingList.join(',').indexOf(environ) > -1) {
          this.environment = staging;

          // is it production environment?
        } else if (environ === 'production') {
          this.environment = production;

          // not valid environment
        } else {
          throw 'environment is not properly defined in init for VisitorTracker';
        }
      }
    };

    /**
     * Description: Gets the url from the window
     * and figures out what the page_name
     * and event_page are for that url. page_name is everything in a url up to
     * the first ? or # symbol. event_page is everything after the # in a url.
     * This data is recorded in eventToSend and returned with it.<br><br>
     *
     * Usage: gets the page_name and event_page parameters for an event from
     * the url<br><br>
     *
     * Precondition: an event needs to get its page_name and event_page defined,
     * and we have a url on our webpage. In that url, no more than 1 #
     * and 1 ? exist.
     * the # occurs after the ? in that url.<br><br>
     *
     * Postcondition: the event returned has its page_name and event_page
     *                properly defined
     *
     * @public
     * @function _getPageNameAndEventPage
     * @memberOf VisitorTracker
     * @instance
     * @global
     *
     * @param {object} eventToSend the event that
     *                             needs to know the page_name and the
     *                             event_page
     * @param {object} addProps original props
     *
     * @returns {object} eventToSend the event with page_name
     *                               and event_page properly
     *                               defined in it
     */
    this._getPageNameAndEventPage = function(eventToSend, addProps) {

      // if there is no eventToSend
      if(!eventToSend) {
        throw 'eventToSend is not defined in _getPageNameAndEventPage' +
        ' in VisitorTracker';
      }

      // set the values properly
      // http://bl.ocks.org/abernier/3070589
      if (!addProps.page_name) {
        eventToSend.page_name = window.location.protocol + window.location.host + window.location.pathname;
      }

      if (!addProps.event_page) {
        eventToSend.event_page = window.location.hash;
      }

      return eventToSend;
    };

    /**
     * Description: looks at each key-value in the url, and assigns the value
     *              properly to
     *              the sessionToSend. Also gets and assigns the hostname.
     *              NOTE: sessionToSend only saves the session body payload
     *              variables on the server. All other key-values that are read
     *              will be ignored by the backend.<br><br>
     *
     * Usage: defines all of the attributes for a Session object that exist
     *        in the url or in window or document objects<br><br>
     *
     * Precondition: we have a url<br><br>
     *
     * Postcondition: the returned Session object has all of its attributes
     *                that were available in the url properly defined
     *
     * @public
     * @function _getSessionData
     * @memberOf VisitorTracker
     * @instance
     * @global
     *
     * @returns {object} sessionToSend a Session object that has all of
     *                   the attributes that
     *                   are in the url properly defined in itself
     */
    this._getSessionData = function() {
      var sessionToSend = new Session();
      var mappings = {
        'gclid': 'click_id'
      };

      // get the host_name first
      sessionToSend.host_name = window.location.hostname;

      // get the http_referrer
      sessionToSend.utm_http_referer = document.referrer;

      var splitByAmpersand = window.location.search.substring(1).split('&');

      for(var i = 0; i < splitByAmpersand.length; i++) {
        var pair = splitByAmpersand[i].split('=');
        var key = pair[0];
        var value = pair[1];

        if (mappings[key]) {
          key = mappings[key];
        }

        sessionToSend[key] = value;
      }

      return sessionToSend;
    };

    /**
     *
     * Description: given an Event object, creates an event object payload
     * and fills in as many attributes for it that are defined in addProps. Any
     * extra attributes get put in the additionalProperties attribute
     * of eventToSend. This is the function that should be used to record and
     * track events.<br><br>
     *
     * Usage: to record data about an event<br><br>
     *
     * Precondition: typeOfEventToTrack is defined. Otherwise it throws
     * an error.<br><br>
     *
     * Postcondition: an event was sent to the server
     *
     * @public
     * @function trackEvent
     * @memberOf VisitorTracker
     * @instance
     * @global
     * @param {object} eventToSend the Event object that is being tracked.
     *
     * @param {object} addProps an object that has attributes that match up
     *                 with the event
     *                 objectPayload. If an event does not match up, it gets
     *                 added to the event object additionalProperties<br><br>
     *
     *                 NOTE: event_action, page_url, page_name, and event_page
     *                 are attributes that cannot be defined in addProps. If they
     *                 are defined there, they will be ignored
     *
     * @param callback {function} the function that wants to use the data, use
     *                 callback(data) to access the event object
     */
    this.trackEvent = function(eventToSend, addProps, callback) {

      // there is no typeOfEventToTrack
      if(!eventToSend) {
        throw 'eventToSend is not defined properly in trackEvent' +
        ' in VisitorTracker';
      }

      // no addProps defined
      if(!addProps) {
        throw 'addProps is not defined properly in trackEvent in VisitorTracker';
      } else {

        // now read in the properties that they defined
        // lead_id, event_element, event_value

        if(addProps.lead_id) {
          eventToSend.set('lead_id', addProps.lead_id);
        }

        if(addProps.event_element) {
          eventToSend.set('event_element', addProps.event_element);
        }

        if(addProps.event_value) {
          eventToSend.set('event_value', addProps.event_value);
        }

        if(addProps.event_element_name) {
          eventToSend.set('event_element_name', addProps.event_element_name);
        }

        // ignore event_action, page_url, page_name, and event_page
        // if they define these values, they are ignored!
        // also ignore all the ones they already defined,
        // additional_properties should not repeat.
        var predefined = ['parent_event_id', 'lead_id', 'page_url',
          'page_name', 'event_page', 'event_action',
          'event_element', 'event_value'];
        for(var key in addProps) {

          // it is not predefined
          if(predefined.indexOf(key) === -1) {

            // it must be an additional property now, add it
            // can't use set for additional properties
            eventToSend.set(key, addProps[key]);
          }
        }

        // eventToSend is all set up now!
        // have to wait for the parent event to be defined
        // before we can send it
        this._postEvent(eventToSend, callback);

        return eventToSend;
      }
    };

    /**
     * Description: given a typeOfEventToTrack, creates an event object payload
     * and fills in as many attributes for it that are defined in addProps. Any
     * extra attributes get put in the additionalProperties attribute
     * of eventToSend. This is the function that should be used to record and
     * track events.<br><br>
     *
     * NOTE: there is no naming convention for events, so mispellings will go
     * through. If you want to record a clivk event, it will record that instead
     * of the click event that you want. This function allows for great modularity
     * and customizability, at the cost of error checking and reliability in
     * its naming conventions.<br><br>
     *
     * Usage: used in conjunction with an EventListener or dispatch for an event,
     *        can be used to then record the data about that event<br><br>
     *
     * Precondition: typeOfEventToTrack is defined. otherwise it returns
     * undefined.<br><br>
     *
     * Postcondition: an event was sent to the server
     *
     * @public
     * @function defineEvent
     * @memberOf VisitorTracker
     * @instance
     * @global
     * @param {string} typeOfEventToTrack the type of event that is being
     *                             tracked. There
     *                             are no requirements or specifications for this
     *                             input.
     *
     * @param {object} addProps an object that has attributes that match up
     *                 with the event
     *                 object payload. If an event does not match up, it gets
     *                 added to the event object additionalProperties<br><br>
     *
     *                 NOTE: event_action, page_url, page_name, and event_page
     *                 are attributes that cannot be defined in addProps. If they
     *                 are defined there, they will be ignored
     *
     * @param {object} parentEvent the parentEvent object of this Event object
     *
     * @param callback {function} the function that wants to use the data, use
     *                 callback(data) to access the event object
     */
    this.defineEvent = function(typeOfEventToTrack, addProps, parentEvent, callback) {

      var eventToReturn;
      if(parentEvent === null) {
        // they don't want a parentEvent
      } else if(parentEvent) {

        // add it to the parent object's children list
        var childIndex = parentEvent.childrenList.length;
        var temp = new Event();
        parentEvent.childrenList.push(temp);
        eventToReturn = parentEvent.childrenList[childIndex];
        // parentEvent.childrenList.push(eventToReturn);
        eventToReturn.parentObject = parentEvent;
        if(parentEvent.get('event_id')){

          // it's already defined! attach its id properly
          eventToReturn.set('parent_event_id', parentEvent.get('event_id'));
        } else {
          // ignore it and wait for it to add itself later
        }
      } else {
        throw 'parentEvent is not defined properly in trackEvent in VisitorTracker';
      }

      if(!eventToReturn) {
        eventToReturn = new Event();
      }

      // first, define the things that we can.
      // define event_action, page_url, page_name, and event_page;
      eventToReturn.set('event_action', typeOfEventToTrack);

      if (!addProps.page_url) {
        eventToReturn.set('page_url', window.location.href);
      }

      eventToReturn.eventPayload = this._getPageNameAndEventPage(eventToReturn.eventPayload, addProps);

      // no addProps defined
      if(!addProps) {
        throw 'addProps is not defined properly in initEvent in VisitorTracker';
      } else {

        // now read in the properties that they defined
        // lead_id, event_element, event_value, event_element_name
        if(addProps.lead_id) {
          eventToReturn.set('lead_id', addProps.lead_id);
        }

        if(addProps.event_element) {
          eventToReturn.set('event_element', addProps.event_element);
        }

        if(addProps.event_value) {
          eventToReturn.set('event_value', addProps.event_value);
        }

        if(addProps.event_element_name) {
          eventToReturn.set('event_element_name', addProps.event_element_name);
        }

        // if they define these values, they are ignored!
        // also ignore all the ones they already defined,
        // additional_properties should not repeat values
        var predefined = ['parent_event_id', 'lead_id',
          'event_action', 'event_element', 'event_value', 'event_element_value',
          'time_difference', 'createdAt'];

        for(var key in addProps) {

          // it is not predefined
          if(predefined.indexOf(key) === -1) {

            // it must be an additional property now, add it
            eventToReturn.set(key, addProps[key]);
          }
        }



        if(typeof(callback) === 'function') {
          callback(eventToReturn);
        }

        return eventToReturn;
      }
    };

    /**
     * Description: A modular way of making an ajax call. Makes any ajax call you
     * want to make by filling out the parameters properly. If the call does not
     * go through, polls every 5 seconds to try to send the data again.
     * doneCallback and callback execute at the same time.<br><br>
     *
     * Usage: to make ajax calls<br><br>
     *
     * Precondition: There is a connection to the
     *               backend through the environment <br><br>
     *
     * Postcondition: The ajax call has been sent properly and data has been
     *                received
     * @public
     * @function _ajaxCall
     * @memberOf VisitorTracker
     * @instance
     * @global
     * @param {string} ajaxCallType GET, PUT, PATCH, POST, DELETE
     *
     * @param {string} ajaxCallPath the path specific for the call type appended
     * to the environment string
     *
     * @param {object} dataToSend the data that should be sent to the backend
     *
     * @param {function} doneCallback the callback that internal functions use to
     * modify variables in the VisitorTracker so that it is easy to see Session
     * and Visitor data without always needing to request.
     *
     * @param {function} callback the callback that the coder can use to perform
     * actions with the data. Should be used like callback(data) and gives you
     * access to data.<br>NOTE: callback should not make any assumptions about
     * changes to the VisitorTracker object as doneCallback and callback are
     * executed at the same time.
     */
    this._ajaxCall = function(ajaxCallType, ajaxCallPath, dataToSend, doneCallback, callback) {
      if(!ajaxCallType || !ajaxCallPath) {
        throw 'ajaxCallType or ajaxCallPath is not defined properly in ' +
        '_ajaxCall in VisitorTracker';
      }

      var FFBillsApi = this.environment + ajaxCallPath;

      if(!dataToSend) {
        throw 'dataToSend is not defined properly in _ajaxCall in VisitorTracker';
      }

      if(ajaxCallType === 'POST' && ajaxCallPath === 'events') {

        // set up the time_difference
        var currentTime = new Date();

        // look at the createdAt and get diff
        dataToSend.time_difference = currentTime.getTime() - dataToSend.createdAt.getTime();
      }


      // create the details for the ajax call
      var ajaxCallToMake = {
        url: FFBillsApi,
        dataType: 'json',
        method: ajaxCallType,
        crossDomain: true,
        xhrFields: {
          withCredentials: true
        }
      };

      // add sending data for POST, PATCH, PUT
      if(ajaxCallType === 'POST' ||
        ajaxCallType === 'PATCH' ||
        ajaxCallType === 'PUT') {
        ajaxCallToMake.data = JSON.stringify(dataToSend);
        ajaxCallToMake.contentType = 'application/json';
      }

      // make the ajax call
      $.ajax(ajaxCallToMake).done($.proxy(function(data, textStatus, xhr) {

        // do my callback to define visitorTracker options
        if(typeof(doneCallback) === 'function') {
          doneCallback(data);
        }

        // do their callback to use the data
        if(typeof(callback) === 'function') {
          callback(null, data);
        }
      }, this)).fail(function(jqxhr, textStatus, error) {

        var err = textStatus + ', ' + error;

        // do their callback to use the data
        if(typeof(callback) === 'function') {
          callback(err, null);
        }
      });
    };

    /**
     * Description: an ajax call to POST a session. POSTing a session also
     *              creates a visitor object associated with the visitor-id
     *              in the session object.
     *              Currently, it also sets the cookie for the session-id
     *              and the visitor-id but that should be removed when
     *              cross-domain functionality works properly.<br>
     *              NOTE: the data returned is a full session object,
     *              not just the
     *              payload that we send. If you modify variables in this
     *              sessionObject and try to send it, the backend should
     *              ignore variables that are not in the session
     *              object payload.<br><br>
     *
     * Usage: to POST a session from the server based upon session-id<br><br>
     *
     * Precondition: We have access to the backend. <br><br>
     *
     * Postcondition: we have set the sessionObject variable in VisitorTracker
     *                based upon data that what we received
     *                and have also returned data.<br><br>
     *
     * @public
     * @function _postSession
     * @memberOf VisitorTracker
     * @instance
     * @global
     * @param {string} sessionId the id of the
     *                           session object we are trying to POST
     *
     * @param {function} callback the function that wants to use the data, use
     *                 callback(data) to access the session object
     */
    this._postSession = function(addProps, callback) {

      if(!addProps) {
        throw 'addProps is not defined properly in _postSession' +
        'in VisitorTracker';

      } else { // they defined addProps
        var ajaxCallType = 'POST';
        var ajaxCallPath = 'session';
        var previousSessionId;
        var previousVisitorId;

        // get the session data from the url that you can
        var dataToSend = this.sessionData || this._getSessionData();

        if (this.ie) {
          if (dataToSend.vt_session_id) {
            this.set('vt_session_id', dataToSend.vt_session_id);
          }

          if (dataToSend.vt_visitor_id) {
            this.set('vt_visitor_id', dataToSend.vt_visitor_id);
          }

          previousSessionId = this.get('vt_session_id');
          previousVisitorId = this.get('vt_visitor_id');

          if (previousSessionId) {
            ajaxCallPath = ajaxCallPath + '/' + previousSessionId + '/ie?requesttype=Update';
          } else {
            ajaxCallPath = ajaxCallPath + '/ie';
          }

          if (previousVisitorId) {
            dataToSend.visitor_id = previousVisitorId;
          }
        }

        delete dataToSend.vt_visitor_id;
        delete dataToSend.vt_session_id;

        // set screen_size
        dataToSend.screen_size = screen.width + 'x' + screen.height;

        // check to see if variation_id and experiment_id are defined
        dataToSend = this.merge({}, dataToSend, addProps);

        var doneCallback = $.proxy(function(data) {

          // get the visitorObject that was made on the server
          this.visitorObject = data.visitor;

          // set the sessionObject
          this.sessionObject = data.session;

          // set sessionCreated flag
          this.sessionCreated = true;

          // save visitor and session ids in localStorage
          this.set('vt_session_id', this.sessionObject.session_id);
          this.set('vt_visitor_id', this.sessionObject.visitor_id);

        }, this);

        this._ajaxCall(
          ajaxCallType,
          ajaxCallPath,
          dataToSend,
          doneCallback,
          callback
        );
      }
    };

    /**
     * Description: an ajax call to PUT a session by the param sessionId. The
     *              variables being modified are read from dataToPut. dataToPut
     *              is not validated for accuracy, it is assumed that it is
     *              an accurate session object payload.<br><br>
     *
     * Usage: to PUT dataToPut into a session object accessed by sessionId<br><br>
     *
     * Precondition: the sessionId is a valid sessionId. We have access to the
     *               backend. dataToPut is defined
     *               and has session data in it.<br><br>
     *
     * Postcondition: we have set the sessionObject variable in VisitorTracker
     *                to what we received based upon the params sessionId and
     *                dataToPut and have also returned data.
     *
     * @public
     * @function _putSession
     * @memberOf VisitorTracker
     * @instance
     * @global
     * @param {string} sessionId the id of the session object
     *                           we are trying to PUT into
     *
     * @param {object} dataToPut the session object
     *                           payload that we are trying to PUT
     *
     * @param {function} callback the function that wants to use the data, use
     *                 callback(data) to access the session object
     */
    this._putSession = function(sessionId, dataToPut, callback) {

      if(!sessionId) {
        throw 'sessionId is not defined properly in _putSession' +
        'in VisitorTracker';

      } else if(!dataToPut) {
        throw 'dataToPut is not defined properly in _putSession' +
        'in VisitorTracker';

      } else { // they defined a sessionId and dataToPut
        var ajaxCallType = 'PUT';
        var ajaxCallPath =  'session/' + sessionId;
        var dataToSend = this.merge({}, this.sessionData || {}, dataToPut);
        var doneCallback = $.proxy(function(data) {

          // set the sessionObject
          this.sessionObject = data.session;

          // set sessionCreated flag
          this.sessionCreated = true;

          // save visitor and session ids in localStorage
          this.set('vt_session_id', this.sessionObject.session_id);
          this.set('vt_visitor_id', this.sessionObject.visitor_id);
        }, this);

        if (this.ie) {
          ajaxCallType = 'POST';
          ajaxCallPath += '/ie?requesttype=Update';
        }

        this._ajaxCall(
          ajaxCallType,
          ajaxCallPath,
          dataToSend,
          doneCallback,
          callback
        );
      }
    };

    /**
     * Description:  an ajax call to POST an event. Use the callback if you want
     *               to assign the event_id to the parent_event_id of another
     *               event. If it fails, it only tries 3 times to post. If the post
     *               succeeds, it goes through. If the parent_event_id is not defined
     *               and the object should have a parent, then the event is not sent.
     *
     *               NOTE: the data returned is a full eventPayload object, not just the
     *               payload that we send. If you modify variables in this
     *               eventPayload and try to send it, the backend should
     *               ignore variables that are not
     *               in the event object payload.<br><br>
     *
     * Usage: to POST an event from the server based upon event-id<br><br>
     *
     * Precondition: We have access to the backend.<br><br>
     *
     * Postcondition: We have set the eventObject variable in VisitorTracker
     *                based upon data that what we received.<br><br>
     *
     * @public
     * @function _postEvent
     * @memberOf VisitorTracker
     * @instance
     * @global
     * @param {object} eventToSend the body object payload of the event we want
     *                    to send to the server
     *
     * @param {function} callback the function that wants to use the data, use
     *                 callback(data) to access the event object
     */
    this._postEvent = function(eventToSend, callback) {
      // Wait until session is created to post events
      if (this.sessionCreated === false) {
        window.setTimeout($.proxy(function () {
          this._postEvent(eventToSend, callback);
        }, this), 100);

        return;
      }

      // We cannot post an event without a session
      if (!this.sessionObject) {
        throw 'cannot post an event without a session';
      }

      // We cannot post an event without an event
      if(!eventToSend) {
        throw 'eventToSend is not defined properly in _postEvent in VisitorTracker';
      }

      var ajaxCallType = 'POST';
      var ajaxCallPath =  'events';
      var dataToSend = eventToSend.eventPayload;

      if (this.ie) {
        ajaxCallPath += '/ie';
        dataToSend.session_id = this.sessionObject.session_id;
      } else {
        ajaxCallPath += '/session/' + this.sessionObject.session_id;
      }

      // we can only send the event if the parent has its own id,
      // or if we have no parents
      if(eventToSend.parentObject === false || eventToSend.get('parent_event_id')) {
        var doneCallback = function (data) {

          // trigger that this event is defined now!
          eventToSend.set('event_id', data.event_id);
          $(eventToSend).trigger('event_idDefined', [data]);
        };

        this._ajaxCall(
          ajaxCallType,
          ajaxCallPath,
          dataToSend,
          doneCallback,
          callback
        );
      } else { // sending failed for some reason

        var count = 0;
        // timeout for 3 times, check each time
        this.intervalId += 1;
        var ourInterval = this.intervalId;
        window['intervalVar' + ourInterval] = window.setInterval(
          $.proxy(
            function () {

              if(count < 3) {
                if(eventToSend.eventPayload.parent_event_id) {

                  // do the ajax call
                  var doneCallback = function (data) {

                    // trigger that this event is defined now!
                    eventToSend.set('event_id', data.event_id);
                    $(eventToSend).trigger('event_idDefined', [data]);
                  };

                  this._ajaxCall(
                    ajaxCallType,
                    ajaxCallPath,
                    dataToSend,
                    doneCallback,
                    callback
                  );
                  count = 3;
                  window.clearInterval(window['intervalVar' + ourInterval]);
                } else {
                  count += 1;
                }

              } else {

                count = 3;
                window.clearInterval(window['intervalVar' + ourInterval]);
              }
            },
          this),
        3000);
      }
    };

    /**
     * Object.assign polyfill
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign#Polyfill
     * @param  {{}} target
     * @return {{}}
     */
    this.merge = function (target) {
      if (target === undefined || target === null) {
        throw new TypeError('Cannot convert undefined or null to object');
      }

      var output = Object(target);
      for (var index = 1; index < arguments.length; index++) {
        var source = arguments[index];
        if (source !== undefined && source !== null) {
          for (var nextKey in source) {
            if (Object.prototype.hasOwnProperty.call(source, nextKey)) {
              output[nextKey] = source[nextKey];
            }
          }
        }
      }
      return output;
    };
  }

  return VisitorTracker;
}));
